<template>
  <div>
    <b-card :title="$t('Login Sessions')">
      <b-row class="justify-content-between align-items-end">
        <b-col
          cols="12"
          lg="5"
          md="7"
          class="mb-1"
        >
          <b-form-group
            :label="$t('Username')"
            label-for="sessionFilter"
            class="mb-0"
          >
            <b-input-group size="sm">
              <b-form-input
                id="sessionFilter"
                v-model="filterUsername"
                type="search"
                :placeholder="$t('Type username to search')"
                @keydown.enter="applyFilters"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  :disabled="loading"
                  @click="applyFilters"
                >
                  {{ $t('Search') }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="3"
          md="5"
          class="mb-1"
        >
          <b-form-group
            :label="$t('Agent')"
            label-for="sessionAgentFilter"
            class="mb-0"
          >
            <agent-selector
              id="sessionAgentFilter"
              v-model="selectedAgent"
              :auto-select="false"
              :lazy="true"
              :placeholder="$t('Type agent to search')"
              size="sm"
              @change="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="4"
          md="12"
          class="mb-1 d-flex justify-content-between justify-content-md-end align-items-center login-session-filter-actions"
        >
          <b-form-checkbox
            v-model="includeRevoked"
            switch
            class="login-session-revoked-toggle"
            @change="applyFilters"
          >
            {{ $t('Show revoked sessions') }}
          </b-form-checkbox>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            size="sm"
            variant="outline-primary"
            class="login-session-refresh-button"
            :disabled="loading"
            @click="reloadData"
          >
            <feather-icon icon="RefreshCwIcon" />
          </b-button>
        </b-col>
      </b-row>

      <div class="login-session-summary mb-1">
        <b-badge variant="light-secondary">
          {{ $t('Results') }} {{ totalRows }}
        </b-badge>
        <b-badge variant="light-success">
          {{ $t('Online') }} {{ counts.online }}
        </b-badge>
        <b-badge variant="light-warning">
          {{ $t('Idle') }} {{ counts.idle }}
        </b-badge>
        <b-badge variant="light-danger">
          {{ $t('Revoked') }} {{ counts.revoked }}
        </b-badge>
        <b-badge variant="light-secondary">
          {{ $t('Expired') }} {{ counts.expired }}
        </b-badge>
      </div>

      <b-table
        ref="table"
        responsive
        striped
        hover
        small
        class="login-session-table"
        :busy.sync="loading"
        :items="sessions"
        :fields="$tFields(fields)"
      >
        <template #head(lastSeenAt)="data">
          <span class="login-session-time-head">
            <span>{{ data.label }}</span>
            <b-button
              v-b-tooltip.hover.top="getTimeToggleTitle()"
              size="sm"
              variant="flat-secondary"
              class="login-session-time-head-toggle"
              :class="{ 'is-active': timeDisplayMode === 'exact' }"
              @click.stop="toggleTimeDisplayMode"
            >
              <feather-icon
                icon="ClockIcon"
                size="12"
              />
              <span class="sr-only">{{ getTimeToggleTitle() }}</span>
            </b-button>
          </span>
        </template>

        <template #head(expiresAt)="data">
          <span class="login-session-time-head">
            <span>{{ data.label }}</span>
            <b-button
              v-b-tooltip.hover.top="getTimeToggleTitle()"
              size="sm"
              variant="flat-secondary"
              class="login-session-time-head-toggle"
              :class="{ 'is-active': timeDisplayMode === 'exact' }"
              @click.stop="toggleTimeDisplayMode"
            >
              <feather-icon
                icon="ClockIcon"
                size="12"
              />
              <span class="sr-only">{{ getTimeToggleTitle() }}</span>
            </b-button>
          </span>
        </template>

        <template #cell(status)="data">
          <b-badge
            :variant="getStatusVariant(data.item)"
            class="text-nowrap"
          >
            {{ getStatusText(data.item) }}
          </b-badge>
        </template>

        <template #cell(username)="data">
          <a
            v-if="getUsernameHref(data.item)"
            :href="getUsernameHref(data.item)"
            target="_blank"
            rel="noopener noreferrer"
            class="login-session-username-link"
            @click.stop
          >
            {{ data.item.username || '-' }}
          </a>
          <span v-else>
            {{ data.item.username || '-' }}
          </span>
        </template>

        <template #cell(userAgent)="data">
          <span
            class="login-session-device"
            :title="data.item.userAgent || '-'"
          >
            {{ formatDevice(data.item.userAgent) }}
          </span>
        </template>

        <template #cell(createAt)="data">
          <span :title="formatDate(data.item.createAt)">
            {{ formatDate(data.item.createAt) }}
          </span>
        </template>

        <template #cell(lastSeenAt)="data">
          <span :title="formatDate(data.item.lastSeenAt)">
            {{ formatLastSeen(data.item.lastSeenAt) }}
          </span>
        </template>

        <template #cell(expiresAt)="data">
          <span :title="formatDate(data.item.expiresAt)">
            {{ formatExpires(data.item.expiresAt) }}
          </span>
        </template>

        <template #cell(actions)="data">
          <div class="login-session-actions">
            <b-button
              v-if="canRevokeSession(data.item)"
              v-b-tooltip.hover.top="getSessionActionTitle(data.item)"
              v-ripple.400="'rgba(234, 84, 85, 0.15)'"
              size="sm"
              variant="outline-danger"
              class="login-session-action"
              @click="revokeSession(data.item)"
            >
              {{ $t('Kick session') }}
            </b-button>
            <span
              v-else
              v-b-tooltip.hover.top="getSessionActionTitle(data.item)"
              class="login-session-action-state"
              :class="getSessionActionStateClass(data.item)"
              tabindex="0"
            >
              {{ getSessionActionStateText(data.item) }}
            </span>
            <b-button
              v-if="canRevokeUser(data.item)"
              v-b-tooltip.hover.top="getUserActionTitle(data.item)"
              v-ripple.400="'rgba(234, 84, 85, 0.15)'"
              size="sm"
              variant="outline-danger"
              class="login-session-action"
              @click="revokeUserSessions(data.item)"
            >
              {{ $t('Kick user') }}
            </b-button>
            <span
              v-else
              v-b-tooltip.hover.top="getUserActionTitle(data.item)"
              class="login-session-action-state"
              :class="getUserActionStateClass(data.item)"
              tabindex="0"
            >
              {{ getUserActionStateText(data.item) }}
            </span>
          </div>
        </template>
      </b-table>

      <b-row class="align-items-center mt-1">
        <b-col
          cols="12"
          md="4"
          class="mb-1 mb-md-0 d-flex align-items-center flex-wrap"
        >
          <b-form-group class="text-nowrap mb-0 mr-1">
            <label class="d-inline-block mr-50 font-small-4">{{ $t('Items') }}</label>
            <b-form-select
              v-model="perPage"
              size="sm"
              :options="pageOptions"
              :disabled="loading"
              class="login-session-page-size"
              @change="onPerPageChange"
            />
          </b-form-group>
          <span class="login-session-page-summary">
            {{ pageSummary }}
          </span>
        </b-col>
        <b-col
          v-if="totalRows > 0"
          cols="12"
          md="8"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            :disabled="loading"
            align="right"
            size="sm"
            class="my-0"
            @change="onPageChange"
          />
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BPagination,
  BRow,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import useJwt from '@/auth/jwt/useJwt'
import {
  onLoginSessionsChanged,
  onSessionRealtimeStateChanged,
} from '@core/auth/session/sessionManager'

const realtimeReloadDebounceMs = 500
const realtimeRefreshIntervalMs = 60000
const fallbackRefreshIntervalMs = 30000
const clockTickIntervalMs = 60000

export default {
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
    BRow,
    BTable,
    AgentSelector,
  },
  data() {
    return {
      loading: false,
      filterUsername: '',
      selectedAgent: null,
      includeRevoked: false,
      timeDisplayMode: this.getStoredTimeDisplayMode(),
      sessions: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      pageOptions: [10, 25, 50, 100, 500],
      counts: {
        all: 0,
        online: 0,
        idle: 0,
        expired: 0,
        revoked: 0,
      },
      currentSid: this.getCurrentSid(),
      currentUsername: this.getCurrentUsername(),
      requestSequence: 0,
      refreshTimer: null,
      realtimeReloadTimer: null,
      clockTimer: null,
      unsubscribeRealtimeChanges: null,
      unsubscribeRealtimeState: null,
      realtimeConnected: false,
      nowTick: Date.now(),
      fields: [
        {
          key: 'status',
          label: 'loginSessions.table.status',
          class: 'text-nowrap text-center',
        },
        {
          key: 'username',
          label: 'loginSessions.table.username',
          class: 'text-nowrap',
        },
        {
          key: 'agent',
          label: 'loginSessions.table.agent',
          class: 'text-nowrap',
        },
        {
          key: 'role',
          label: 'loginSessions.table.role',
          class: 'text-nowrap',
        },
        {
          key: 'ip',
          label: 'loginSessions.table.ip',
          class: 'text-nowrap',
        },
        {
          key: 'userAgent',
          label: 'loginSessions.table.device',
          class: 'text-nowrap',
        },
        {
          key: 'createAt',
          label: 'loginSessions.table.createdAt',
          class: 'text-nowrap',
        },
        {
          key: 'lastSeenAt',
          label: 'loginSessions.table.lastSeen',
          class: 'text-nowrap',
        },
        {
          key: 'expiresAt',
          label: 'loginSessions.table.expiresAt',
          class: 'text-nowrap',
        },
        {
          key: 'actions',
          label: 'loginSessions.table.actions',
          class: 'text-nowrap text-center',
        },
      ],
    }
  },
  computed: {
    pageSummary() {
      if (!this.totalRows) return this.$t('No sessions')

      const from = (this.currentPage - 1) * this.perPage + 1
      const to = Math.min(this.currentPage * this.perPage, this.totalRows)
      return this.$t('Showing sessions', {
        from,
        to,
        total: this.totalRows,
      })
    },
  },
  mounted() {
    this.reloadData()
    this.startClock()
    this.unsubscribeRealtimeChanges = onLoginSessionsChanged(this.scheduleRealtimeReload)
    this.unsubscribeRealtimeState = onSessionRealtimeStateChanged(this.handleRealtimeStateChange)
  },
  beforeDestroy() {
    this.stopRefreshTimer()
    this.stopClock()
    if (this.realtimeReloadTimer) window.clearTimeout(this.realtimeReloadTimer)
    if (this.unsubscribeRealtimeChanges) this.unsubscribeRealtimeChanges()
    if (this.unsubscribeRealtimeState) this.unsubscribeRealtimeState()
  },
  methods: {
    async reloadData() {
      const sequence = this.requestSequence + 1
      this.requestSequence = sequence
      this.loading = true
      try {
        const response = await useJwt.loginSessions({
          username: this.filterUsername || undefined,
          agent: this.selectedAgent && this.selectedAgent.code ? this.selectedAgent.code : undefined,
          includeRevoked: this.includeRevoked,
          page: this.currentPage,
          perPage: this.perPage,
        })
        if (sequence !== this.requestSequence) return

        const payload = response.data || {}
        const sessions = payload.list || []
        const total = this.pickPayloadValue(payload, ['total', 'Total'])
        const page = this.pickPayloadValue(payload, ['page', 'Page'])
        const perPage = this.pickPayloadValue(payload, ['perPage', 'PerPage'])
        const counts = this.pickPayloadValue(payload, ['counts', 'Counts'])

        this.sessions = sessions
        this.totalRows = total === undefined ? sessions.length : Number(total || 0)
        this.currentPage = Number(page || this.currentPage)
        this.perPage = Number(perPage || this.perPage)
        this.counts = this.buildCounts(counts, sessions)
        if (this.sessions.length === 0 && this.totalRows > 0 && this.currentPage > 1) {
          this.currentPage = Math.ceil(this.totalRows / this.perPage)
          await this.reloadData()
        }
      } catch (error) {
        if (sequence === this.requestSequence) {
          this.showToast('danger', 'AlertCircleIcon', this.$t('Unable to load login sessions'))
        }
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    applyFilters() {
      this.currentPage = 1
      this.reloadData()
    },
    onPageChange(page) {
      this.currentPage = page
      this.reloadData()
    },
    onPerPageChange() {
      this.currentPage = 1
      this.reloadData()
    },
    handleRealtimeStateChange(isConnected) {
      this.realtimeConnected = isConnected
      this.startRefreshTimer()
    },
    scheduleRealtimeReload() {
      if (this.realtimeReloadTimer) return
      this.realtimeReloadTimer = window.setTimeout(() => {
        this.realtimeReloadTimer = null
        this.reloadData()
      }, realtimeReloadDebounceMs)
    },
    startRefreshTimer() {
      this.stopRefreshTimer()
      const interval = this.realtimeConnected ? realtimeRefreshIntervalMs : fallbackRefreshIntervalMs
      this.refreshTimer = window.setInterval(() => {
        this.reloadData()
      }, interval)
    },
    stopRefreshTimer() {
      if (!this.refreshTimer) return
      window.clearInterval(this.refreshTimer)
      this.refreshTimer = null
    },
    startClock() {
      if (this.clockTimer) return
      this.clockTimer = window.setInterval(() => {
        this.nowTick = Date.now()
      }, clockTickIntervalMs)
    },
    stopClock() {
      if (!this.clockTimer) return
      window.clearInterval(this.clockTimer)
      this.clockTimer = null
    },
    pickPayloadValue(payload, keys) {
      return keys.reduce((value, key) => {
        if (value !== undefined) return value
        return Object.prototype.hasOwnProperty.call(payload, key) ? payload[key] : undefined
      }, undefined)
    },
    buildCounts(counts, sessions) {
      const fallback = this.countSessions(sessions)
      const source = counts || {}
      const all = source.all === undefined ? source.total : source.all
      return {
        all: Number(all === undefined ? fallback.all : all),
        online: Number(source.online === undefined ? fallback.online : source.online),
        idle: Number(source.idle === undefined ? fallback.idle : source.idle),
        expired: Number(source.expired === undefined ? fallback.expired : source.expired),
        revoked: Number(source.revoked === undefined ? fallback.revoked : source.revoked),
      }
    },
    countSessions(sessions) {
      return sessions.reduce((total, item) => {
        const next = total
        next.all += 1
        if (item.revokedAt) next.revoked += 1
        else if (!item.active) next.expired += 1
        else if (item.online) next.online += 1
        else next.idle += 1
        return next
      }, {
        all: 0,
        online: 0,
        idle: 0,
        expired: 0,
        revoked: 0,
      })
    },
    canRevokeSession(item) {
      return item.active && item.sid && item.sid !== this.currentSid
    },
    canRevokeUser(item) {
      const username = this.normalizeUsername(item.username)
      return username && username !== this.currentUsername
    },
    getUsernameRoute(item) {
      const username = (item.username || '').trim()
      if (!username) return null
      if (this.normalizeUsername(username) === this.currentUsername) {
        return { name: 'profile' }
      }

      switch (this.normalizeUsername(item.role)) {
        case 'admin':
          return { name: 'agents-edit', query: { id: username } }
        case 'sub':
          return { name: 'sub-account-edit', query: { id: username } }
        case 'member':
        case 'customer':
          return { name: 'members-edit', query: { id: username } }
        default:
          return null
      }
    },
    getUsernameHref(item) {
      const route = this.getUsernameRoute(item)
      return route ? this.$router.resolve(route).href : null
    },
    getStatusVariant(item) {
      if (item.revokedAt) return 'danger'
      if (!item.active) return 'secondary'
      return item.online ? 'success' : 'warning'
    },
    getStatusText(item) {
      if (item.revokedAt) return this.$t('Revoked')
      if (!item.active) return this.$t('Expired')
      return item.online ? this.$t('Online') : this.$t('Idle')
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString(this.getLocale(), { hour12: false })
    },
    formatLastSeen(value) {
      if (!value) return '-'
      if (this.timeDisplayMode === 'exact') return this.formatDate(value)
      return this.formatElapsed(value)
    },
    formatExpires(value) {
      if (!value) return '-'
      if (this.timeDisplayMode === 'exact') return this.formatDate(value)
      const seconds = Math.floor((new Date(value).getTime() - this.nowTick) / 1000)
      if (seconds <= 0) return this.$t('Expired')
      return this.formatDuration(seconds, true)
    },
    formatElapsed(value) {
      const seconds = Math.max(0, Math.floor((this.nowTick - new Date(value).getTime()) / 1000))
      if (seconds < 60) return this.$t('Just now')
      return this.formatDuration(seconds, false)
    },
    formatDuration(seconds, future) {
      const units = [
        { name: 'day', value: 86400 },
        { name: 'hour', value: 3600 },
        { name: 'minute', value: 60 },
      ]
      const unit = units.find(item => seconds >= item.value) || units[units.length - 1]
      const amount = Math.max(1, Math.floor(seconds / unit.value))
      try {
        const locale = (this.$i18n && this.$i18n.locale) || navigator.language || 'en'
        return new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
          style: 'short',
        }).format(future ? amount : -amount, unit.name)
      } catch (error) {
        return future ? `in ${amount}${unit.name[0]}` : `${amount}${unit.name[0]} ago`
      }
    },
    formatDevice(value) {
      if (!value) return '-'
      return `${this.detectDeviceOs(value)} / ${this.detectDeviceBrowser(value)}`
    },
    detectDeviceOs(value) {
      if (/iPhone|iPad|iPod/i.test(value)) return 'iOS'
      if (/Android/i.test(value)) return 'Android'
      if (/Windows NT/i.test(value)) return 'Windows'
      if (/Macintosh|Mac OS X/i.test(value)) return 'macOS'
      if (/Linux/i.test(value)) return 'Linux'
      return this.$t('Unknown OS')
    },
    detectDeviceBrowser(value) {
      if (/Edg\//i.test(value)) return 'Edge'
      if (/OPR\//i.test(value)) return 'Opera'
      if (/CriOS\//i.test(value) || /Chrome\//i.test(value) || /Chromium\//i.test(value)) return 'Chrome'
      if (/FxiOS\//i.test(value) || /Firefox\//i.test(value)) return 'Firefox'
      if (/Safari\//i.test(value)) return 'Safari'
      if (/PostmanRuntime/i.test(value)) return 'Postman'
      return this.$t('Browser')
    },
    async revokeSession(item) {
      const confirmed = await this.confirmRevoke(this.$t('Kick this session?'))
      if (!confirmed) return
      try {
        await useJwt.revokeSession(item.sid, 'master-force-logout')
        this.showToast('success', 'CheckIcon', this.$t('Session revoked'))
        await this.reloadData()
      } catch (error) {
        this.showToast('danger', 'AlertCircleIcon', this.$t('Unable to revoke session'))
      }
    },
    async revokeUserSessions(item) {
      const confirmed = await this.confirmRevoke(this.$t('Kick all sessions for this user?'))
      if (!confirmed) return
      try {
        await useJwt.revokeUserSessions(item.username, 'master-force-logout')
        this.showToast('success', 'CheckIcon', this.$t('User sessions revoked'))
        await this.reloadData()
      } catch (error) {
        this.showToast('danger', 'AlertCircleIcon', this.$t('Unable to revoke user sessions'))
      }
    },
    async confirmRevoke(text) {
      const result = await this.$swal({
        dark: true,
        title: this.$t('Are you sure?'),
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Ok'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      return result.value
    },
    showToast(variant, icon, text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: variant === 'success' ? this.$t('success') : this.$t('Error'),
          icon,
          variant,
          text,
        },
      })
    },
    getCurrentSid() {
      const token = useJwt.getToken()
      if (!token) return ''
      try {
        const payload = this.decodeJwtPayload(token)
        return payload.sid || ''
      } catch (error) {
        return ''
      }
    },
    getCurrentUsername() {
      const token = useJwt.getToken()
      try {
        if (token) {
          const payload = this.decodeJwtPayload(token)
          return this.normalizeUsername(payload.username)
        }
      } catch (error) {
        // Fall back to userData below.
      }

      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        return this.normalizeUsername(userData.username)
      } catch (error) {
        return ''
      }
    },
    decodeJwtPayload(token) {
      let encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
      while (encoded.length % 4) encoded += '='
      return JSON.parse(window.atob(encoded))
    },
    normalizeUsername(value) {
      return (value || '').trim().toLowerCase()
    },
    getSessionActionTitle(item) {
      if (item.revokedAt) return this.$t('Session is already revoked')
      if (!item.active) return this.$t('Session is already expired')
      if (!item.sid) return this.$t('Session id is unavailable')
      return item.sid === this.currentSid
        ? this.$t('Current session cannot be kicked here')
        : this.$t('Kick only this session')
    },
    getSessionActionStateText(item) {
      if (item.revokedAt) return this.$t('Already revoked')
      if (!item.active) return this.$t('Already expired')
      return item.sid === this.currentSid
        ? this.$t('Current session')
        : this.$t('Unavailable')
    },
    getSessionActionStateClass(item) {
      return {
        'login-session-action-state--current': item.sid === this.currentSid,
        'login-session-action-state--revoked': !!item.revokedAt,
        'login-session-action-state--expired': !item.revokedAt && !item.active,
        'login-session-action-state--unavailable': item.active && !item.sid,
      }
    },
    getUserActionTitle(item) {
      if (this.canRevokeUser(item)) return this.$t('Kick all sessions for this user')
      return this.normalizeUsername(item.username)
        ? this.$t('Current user cannot be kicked here')
        : this.$t('Username is unavailable')
    },
    getUserActionStateText(item) {
      return this.normalizeUsername(item.username)
        ? this.$t('Current user')
        : this.$t('Unavailable')
    },
    getUserActionStateClass(item) {
      const username = this.normalizeUsername(item.username)
      return {
        'login-session-action-state--current': !!username,
        'login-session-action-state--unavailable': !username,
      }
    },
    getLocale() {
      return (this.$i18n && this.$i18n.locale) || navigator.language || 'en'
    },
    getStoredTimeDisplayMode() {
      try {
        const value = localStorage.getItem('loginSessionTimeDisplayMode')
        return ['relative', 'exact'].includes(value) ? value : 'relative'
      } catch (error) {
        return 'relative'
      }
    },
    setTimeDisplayMode(mode) {
      if (!['relative', 'exact'].includes(mode)) return
      this.timeDisplayMode = mode
      try {
        localStorage.setItem('loginSessionTimeDisplayMode', mode)
      } catch (error) {
        // Ignore storage errors; the current view can still switch modes.
      }
    },
    toggleTimeDisplayMode() {
      this.setTimeDisplayMode(this.timeDisplayMode === 'relative' ? 'exact' : 'relative')
    },
    getTimeToggleTitle() {
      return this.timeDisplayMode === 'relative'
        ? this.$t('Show exact time')
        : this.$t('Show relative time')
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';

.login-session-device {
  display: inline-block;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.login-session-table.table.b-table > tbody > tr > td {
  height: 3rem;
  vertical-align: middle;
}

.login-session-username-link {
  color: inherit;
  text-decoration: none;
  text-underline-offset: .18rem;
}

.login-session-username-link:hover,
.login-session-username-link:focus {
  color: #ea5455;
  text-decoration: underline;
}

.login-session-summary {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem;
}

.login-session-time-head {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .25rem;
}

.login-session-time-head-toggle {
  width: 1.35rem;
  height: 1.35rem;
  min-width: 1.35rem;
  padding: 0;
  color: #a19aa8;
  line-height: 1;
}

.login-session-time-head-toggle.is-active {
  color: #6e6b7b;
  background: rgba(110, 107, 123, .1);
}

.login-session-filter-actions {
  gap: 1rem;
  min-height: 2.142rem;
}

.login-session-revoked-toggle {
  display: inline-flex;
  align-items: center;
  min-height: 2.142rem;
  margin-right: 0;
  margin-bottom: 0;
}

.login-session-revoked-toggle .custom-control-label {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0;
  line-height: 1.2;
}

.login-session-revoked-toggle.custom-switch:not(.custom-control-inline) .custom-control-label {
  margin-bottom: 0;
}

.login-session-revoked-toggle.custom-switch .custom-control-label::before,
.login-session-revoked-toggle.custom-switch .custom-control-label::after {
  top: 50%;
  translate: 0 -50%;
}

.login-session-refresh-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.15rem;
  height: 2.142rem;
  padding: 0;
  line-height: 1;
}

.login-session-actions {
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(7.25rem, max-content));
  align-items: center;
  justify-items: center;
  gap: .5rem;
}

.login-session-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 7.25rem;
  height: 2.15rem;
  min-height: 2.15rem;
  padding: 0 .75rem;
  font-weight: 600;
  line-height: 1.1;
  box-sizing: border-box;
}

.login-session-action-state {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 7.25rem;
  height: 2.15rem;
  min-height: 2.15rem;
  padding: 0 .75rem;
  border: 1px solid transparent;
  border-radius: .358rem;
  background: rgba(110, 107, 123, .08);
  color: #8a8492;
  font-size: .75rem;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
  box-sizing: border-box;
}

.login-session-action-state--current {
  background: rgba(110, 107, 123, .08);
}

.login-session-action-state--revoked {
  background: rgba(234, 84, 85, .1);
  color: #ea5455;
}

.login-session-action-state--expired {
  background: rgba(255, 159, 67, .12);
  color: #b66b16;
}

.login-session-action-state--unavailable {
  color: #a19aa8;
}

.login-session-action-state:focus {
  outline: none;
  box-shadow: 0 0 0 .15rem rgba(110, 107, 123, .12);
}

.login-session-page-size {
  display: inline-block;
  width: 5rem;
}

.login-session-page-summary {
  color: #6e6b7b;
  font-size: .857rem;
}
</style>
